export default function AIPathway() {
  const pathwaySteps = [
    {
      status: 'completed',
      title: 'Completed',
      subtitle: 'AI Foundations',
      icon: 'ri-checkbox-circle-fill',
      color: 'text-green-500',
      bgColor: 'bg-green-50',
    },
    {
      status: 'in-progress',
      title: 'In Progress',
      subtitle: 'Digital Communication',
      icon: 'ri-loader-4-line',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      progress: 65,
    },
    {
      status: 'recommended',
      title: 'AI Recommended',
      subtitle: 'Creative Thinking',
      icon: 'ri-lightbulb-flash-line',
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
    },
    {
      status: 'advanced',
      title: 'Advanced Specialization',
      subtitle: 'Business Essentials',
      icon: 'ri-star-line',
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
    },
    {
      status: 'goal',
      title: 'Pathway Goal',
      subtitle: 'UX Design Bootcamp',
      icon: 'ri-trophy-line',
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-50',
    },
  ];

  const features = [
    {
      icon: 'ri-chat-smile-3-line',
      title: 'AI Tutor',
      description: 'Your AI tutor answers questions and suggests what to learn next, step by step.',
      gradient: 'from-[#FF6B9D] to-[#FFA726]',
    },
    {
      icon: 'ri-compass-3-line',
      title: 'Smart Navigation',
      description: 'AI guides you to the next best microcourse based on your progress and goals.',
      gradient: 'from-[#304FFE] to-[#5B9FED]',
    },
    {
      icon: 'ri-focus-3-line',
      title: 'Goal-Oriented',
      description: 'Set your target and let Praxia build a clear roadmap toward your degree or career.',
      gradient: 'from-[#7C4DFF] to-[#B794F6]',
    },
    {
      icon: 'ri-refresh-line',
      title: 'Always Adapting',
      description: 'Your pathway updates as you grow, unlocking new options as your skills evolve.',
      gradient: 'from-[#00BFA5] to-[#00E676]',
    },
  ];

  return (
    <section className="py-28 px-6 lg:px-12 relative overflow-hidden">
      {/* Animated gradient background - same as Hero */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#304FFE]/[0.08] via-transparent to-[#7C4DFF]/[0.08]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
      </div>

      {/* Floating decorative elements - same as Hero */}
      <div className="absolute top-1/4 right-[10%] w-64 h-64 bg-gradient-to-br from-[#304FFE]/20 to-[#7C4DFF]/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 left-[15%] w-48 h-48 bg-gradient-to-br from-[#7C4DFF]/15 to-[#304FFE]/15 rounded-full blur-3xl animate-pulse delay-1000"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl lg:text-6xl font-bold text-[#1A1F36] mb-6">
            Your personalized learning journey, powered by AI.
          </h2>
          <p className="text-2xl text-gray-600 max-w-4xl mx-auto font-light leading-relaxed">
            Praxia uses AI to understand your skills, learning style and progress, creating a dynamic pathway that adapts as you grow — and your AI Tutor guides you step by step with personalized recommendations, answers and support.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: Pathway UI */}
          <div className="bg-white rounded-3xl p-10 shadow-lg border border-gray-100">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gradient-to-br from-[#7C4DFF] to-[#B794F6] rounded-xl flex items-center justify-center">
                <i className="ri-route-line text-xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A1F36]">Your Pathway</h3>
            </div>

            <div className="space-y-6">
              {pathwaySteps.map((step, index) => (
                <div key={index} className="relative">
                  {/* Connector line */}
                  {index < pathwaySteps.length - 1 && (
                    <div className="absolute left-6 top-14 w-0.5 h-10 bg-gray-200"></div>
                  )}

                  <div className="flex items-start gap-4 group cursor-pointer">
                    {/* Icon */}
                    <div className={`w-12 h-12 ${step.bgColor} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                      <i className={`${step.icon} text-2xl ${step.color}`}></i>
                    </div>

                    {/* Content */}
                    <div className="flex-grow">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-lg font-bold text-[#1A1F36]">{step.title}</h4>
                        {step.status === 'completed' && (
                          <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">
                            ✓ Done
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{step.subtitle}</p>

                      {/* Progress bar for in-progress */}
                      {step.progress && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-gray-500">Progress</span>
                            <span className="text-xs font-bold text-blue-600">{step.progress}%</span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500"
                              style={{ width: `${step.progress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Feature Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 relative overflow-hidden group h-full flex flex-col"
              >
                {/* Subtle glow effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

                {/* Content */}
                <div className="relative z-10 flex flex-col h-full">
                  {/* Icon */}
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg flex-shrink-0`}>
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>

                  {/* Title */}
                  <h4 className="text-xl font-bold text-[#1A1F36] mb-3 flex-shrink-0">
                    {feature.title}
                  </h4>

                  {/* Description */}
                  <p className="text-sm text-gray-600 leading-relaxed flex-grow">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
